import { useCreateAssessor } from "@/api/console/assessor/useCreateAssessor";
import InputGroup from "@/components/InputGroup";
import MultiSelect from "@/components/MultiSelect";
import Select from "@/components/Select";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ROLE } from "@/config/role";
import { emailPattern } from "@/lib/regexPattern";
import toast from "@/lib/toast";
import {
  AssessorCreate,
  assessorCreateSchema,
} from "@/schema/console/assessors/AssessorCreate";
import useAssessorDialog from "@/src/console/(auth)/assessors/_hooks/useAssessorDialog";
import useGetCompanyOptions from "@/src/console/(auth)/assessors/_hooks/useGetCompanyOptiions";
import { SpinnerIosRegular } from "@fluentui/react-icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { mutate } from "swr";

function CreateAssessorDialog() {
  const { t } = useTranslation();
  const { type, resetDialog } = useAssessorDialog();

  const { companyOptions } = useGetCompanyOptions();

  const { trigger, isMutating } = useCreateAssessor({
    onSuccess: (data) => {
      toast.success(data.message);
      mutate("/console/admin_users");
      resetDialog();
      reset();
    },
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AssessorCreate>({
    resolver: zodResolver(assessorCreateSchema),
    defaultValues: {
      role: 1,
      company_ids: [],
    },
  });

  const { field: roleField } = useController({
    control,
    name: "role",
  });

  const { field: companyField } = useController({
    control,
    name: "company_ids",
  });

  return (
    <div>
      <Dialog
        open={type === "create"}
        onOpenChange={(open) => !open && resetDialog()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("register-new-member")}</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              handleSubmit(({ company_ids, role, ...rest }) => {
                trigger({
                  ...rest,
                  role,
                  company_ids: role === ROLE.ASSESSOR ? company_ids : [],
                });
              })();
            }}
          >
            <div className="flex flex-col gap-6 px-6 pb-5 pt-4">
              <InputGroup
                label={t("name")}
                name="name"
                required
                error={errors.name?.message}
                register={register("name", {
                  required: t("name-required"),
                })}
              />
              <InputGroup
                label={t("email")}
                name="email"
                required
                error={errors.email?.message}
                register={register("email", {
                  required: t("email-required"),
                  pattern: {
                    value: emailPattern,
                    message: t("enter-mail-message"),
                  },
                })}
                className="col-span-2"
                type="email"
              />
              <div>
                <div className="mb-1">{t("role")}</div>
                <Select
                  placeholder="Select role"
                  className="col-span-2 rounded-md border"
                  value={`${roleField.value}`}
                  onChange={(value) => roleField.onChange(value)}
                  options={[
                    { value: "0", label: t("admin") },
                    { value: "1", label: t("assessor") },
                  ]}
                />
                {errors.role && (
                  <p className="text-red-500">{errors.role.message}</p>
                )}
              </div>
              {roleField.value === ROLE.ASSESSOR && (
                <div>
                  <MultiSelect
                    value={companyField.value}
                    onChange={(value) => companyField.onChange(value)}
                    placeholder={t("companies")}
                    data={companyOptions}
                  />
                  <div>
                    {companyField.value.map((company_id) => (
                      <div key={company_id}>
                        {
                          companyOptions.find(
                            (company) => company.value === company_id,
                          )?.label
                        }
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <InputGroup
                required
                label={t("password")}
                name="password"
                type="password"
                error={errors.password?.message}
                register={register("password", {
                  required: t("password-required"),
                  validate: (value) => {
                    if (!value) return t("password-required");
                    if (value.length < 8) {
                      return t("Password-digit-message");
                    }
                  },
                })}
              />
              <Button
                isLoading={isMutating}
                LoadingComponent={
                  <SpinnerIosRegular className="animate-spin" />
                }
                type="submit"
              >
                {t("register")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CreateAssessorDialog;
